import {
  Autocomplete,
  Box,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useContext, useMemo } from 'react';

import RequiredIndicator from '@/components/common/RequiredIndicator';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { PartnerIntakeContext } from '@/Contexts/PartnerIntakeContext';
import { CountryProps } from '@/models/Country';

import RowWrap from '../common/IntakeRowWrap';

function IntakeCountrySelect() {
  const theme = useTheme();

  const { countryId, partnerInfo, showErrors, updatePartnerInfo, updateCountryId } =
    useContext(PartnerIntakeContext);

  const { translatedText } = useContext(LocaleContext);
  const { allCountries, countrySelectOptions } = useContext(ConfigContext);

  const selectedCountry = useMemo(() => {
    if (countryId != null && allCountries != null) {
      return allCountries.find((country: CountryProps) => country.id === countryId) ?? null;
    }
    return null;
  }, [allCountries, countryId]);

  return (
    <RowWrap>
      <Box width='100%'>
        <Autocomplete
          id='intake-country-select'
          options={countrySelectOptions}
          getOptionLabel={(option: { label: string; value: string }) => option.label}
          autoComplete={false}
          value={{
            label: selectedCountry?.name ?? '',
            value: selectedCountry?.id?.toString() ?? '',
          }}
          onChange={(e, country: { label: string; value: string } | null) => {
            const selectedCountryId: number | null =
              country?.value != null ? Number(country?.value) : null;
            updateCountryId(selectedCountryId);

            // check if selectedCountryId is a number
            if (selectedCountryId !== null && !isNaN(selectedCountryId)) {
              updatePartnerInfo({
                ...partnerInfo,
                countryId: selectedCountryId,
              });
            }
          }}
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          )}
          slotProps={{
            paper: {
              style: {
                maxHeight: '200px',
                marginTop: '4px',
                overflow: 'auto',
              },
            },
            listbox: {
              style: {
                overflow: 'visible',
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <Typography variant='subtitle2'>
                  {translatedText.contact.labelCountry}
                  <RequiredIndicator />
                </Typography>
              }
              variant='outlined'
              slotProps={{
                htmlInput: { ...params.inputProps, autoComplete: 'off' },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '49.33px',
                },
                '.MuiOutlinedInput-input': {
                  paddingLeft: '.5rem',
                },
                ...(showErrors &&
                  selectedCountry === null && {
                    '& .MuiOutlinedInput-root': {
                      borderColor: theme.palette.error.main,
                    },
                  }),
              }}
            />
          )}
        />
      </Box>
    </RowWrap>
  );
}

export default IntakeCountrySelect;
